import React, { useRef } from "react";
import Rodal from "rodal";
import { observer, inject } from "mobx-react";
import Markdown from "markdown-to-jsx";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper";

import "swiper/css";
import "rodal/lib/rodal.css";
import "./AppDialog.css";

import AppDialogStore from "../../mobx/AppDialogStore";
import { resolveStrapiImage } from "../../services/image-url";
import YellowButton from "../Yellow-Button/Yellow-Button";
import StepIndicator from "../StepIndicator/StepIndicator";

interface AppDialogProps {
  appDialogStore?: AppDialogStore;
}

const SMALL_SCREEN_BREAKPOINT = 430;
const TALL_DIALOG_HEIGHT = 600;

const AppDialogView = ({ appDialogStore }: AppDialogProps) => {
  const swiperRef = useRef<SwiperClass | null>(null);

  if (!appDialogStore || !appDialogStore.currentDialog) {
    return null;
  }

  const dialog = appDialogStore.currentDialog;
  const slides = dialog.slides || [];
  const totalSlides = slides.length;
  if (totalSlides === 0) return null;

  const slideIndex = Math.min(appDialogStore.slideIndex, totalSlides - 1);
  const isMultiSlide = totalSlides > 1;
  const isLastSlide = slideIndex === totalSlides - 1;
  const dismissLabel = (dialog.dismissButtonText || "").trim();
  const showButton = !isMultiSlide ? dismissLabel.length > 0 : true;
  const anySlideHasImage = slides.some((s) => resolveStrapiImage(s.image));

  let compactHeight = 200;
  if (showButton) compactHeight += 90;
  if (isMultiSlide) compactHeight += 24;

  const dialogHeight =
    anySlideHasImage || isMultiSlide
      ? Math.min(window.innerHeight - 40, TALL_DIALOG_HEIGHT)
      : Math.min(window.innerHeight - 40, compactHeight);

  const dialogWidth =
    window.innerWidth < SMALL_SCREEN_BREAKPOINT
      ? window.innerWidth - 30
      : 400;

  const buttonLabel = isLastSlide ? dismissLabel || "Weiter" : "Weiter";

  const handlePrimary = () => {
    if (isLastSlide) {
      appDialogStore.dismissDialog();
    } else if (swiperRef.current) {
      swiperRef.current.slideNext();
    } else {
      appDialogStore.showNextSlide();
    }
  };

  const renderSlide = (slide, idx: number) => {
    const resolved = resolveStrapiImage(slide.image);
    const hasImage = !!resolved;
    return (
      <div
        className={
          "app-dialog-slide" +
          (hasImage ? "" : " app-dialog-slide--text-only")
        }
        key={idx}
      >
        {hasImage && (
          <div
            className="app-dialog-slide__image"
            style={{ backgroundImage: `url("${encodeURI(resolved!.src)}")` }}
          />
        )}
        <div className="app-dialog-slide__body">
          {slide.title && (
            <div className="app-dialog-slide__title">{slide.title}</div>
          )}
          {slide.text && (
            <div className="app-dialog-slide__text">
              <Markdown
                options={{
                  overrides: {
                    a: {
                      props: { target: "_blank", rel: "noopener noreferrer" },
                    },
                  },
                }}
              >
                {slide.text}
              </Markdown>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Rodal
      className="app-dialog-container"
      visible={true}
      animation="zoom"
      showCloseButton={false}
      width={dialogWidth}
      height={dialogHeight}
      onClose={() => {
        appDialogStore.dismissDialog();
      }}
    >
      <div className="app-dialog-shell">
        <div className="app-dialog-shell__content">
          {isMultiSlide ? (
            <Swiper
              className="app-dialog-swiper"
              initialSlide={slideIndex}
              onSwiper={(s) => {
                swiperRef.current = s;
              }}
              onSlideChange={(s) => {
                appDialogStore.setSlideIndex(s.activeIndex);
              }}
              spaceBetween={0}
              slidesPerView={1}
            >
              {slides.map((s, i) => (
                <SwiperSlide key={i}>{renderSlide(s, i)}</SwiperSlide>
              ))}
            </Swiper>
          ) : (
            renderSlide(slides[0], 0)
          )}
        </div>
        {(showButton || isMultiSlide) && (
          <div className="app-dialog-shell__footer">
            {isMultiSlide && (
              <div className="app-dialog-slide__steps">
                <StepIndicator step={slideIndex} maximumSteps={totalSlides} />
              </div>
            )}
            {showButton && (
              <YellowButton label={buttonLabel} onClick={handlePrimary} />
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        className="app-dialog-close"
        aria-label="Close"
        onClick={() => {
          appDialogStore.dismissDialog();
        }}
      />
    </Rodal>
  );
};

export default inject("appDialogStore")(observer(AppDialogView));
